import { retryUnlessClientError } from '@brush/api-client';
import { ActingRoleProvider, AuthProvider, RequireAuth } from '@brush/auth';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, useState, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { DevApiProvider, MsalApiProvider } from './api';
import { App } from './App';
import { apiScope, authDisabled, msalConfig } from './authConfig';
import { LandingScreen } from './components/LandingScreen';
import { PeriodsProvider } from './periods/context';
import { RoomProvider } from './room';
import { UnsavedProvider } from './unsaved';
import './index.css';

const queryClient = new QueryClient({
  // A 4xx is an answer and is never asked again; anything else once (#128, `retryUnlessClientError`). A hook
  // stating its own `retry` still wins.
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: retryUnlessClientError } },
});

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root not found.');

// Outside RoomProvider: switching room is itself a way to lose a draft, so the guard has to
// exist before anything that can change the room.
const app: ReactNode = (
  <UnsavedProvider>
    {/*
      Inside the unsaved-work guard and outside RoomProvider, for the same reason the room
      switcher is both: changing role is a way out of an editor, and it changes which rooms
      there are to resolve between.
    */}
    <ActingRoleProvider>
      {/*
        Above RoomProvider: a period chosen for the Rundown or the Overview is the session's, not the room's,
        so it applies in the next room too — and switching room must not remount it (UP25).
      */}
      <PeriodsProvider>
        <RoomProvider>
          <App />
        </RoomProvider>
      </PeriodsProvider>
    </ActingRoleProvider>
  </UnsavedProvider>
);

/**
 * The dev bypass has no sign-in gate at all, so the door has no moment to occupy and would be
 * unreachable on the one setup that needs no tenant — which is the setup it will be worked on
 * from. This puts it in front, once per tab.
 *
 * Session storage rather than local: the door is not a preference, and somebody who has just
 * changed it wants to see it again on the next reload of a fresh tab. It is a development
 * affordance and not a feature — with MSAL on, `RequireAuth` decides, and being signed in is what
 * dismisses the page.
 */
const DEV_ENTERED_KEY = 'brush.room.devLandingEntered';

function DevLandingGate({ children }: { children: ReactNode }) {
  const [entered, setEntered] = useState(() => sessionStorage.getItem(DEV_ENTERED_KEY) === 'true');

  if (entered) return <>{children}</>;

  return (
    <LandingScreen
      onSignIn={() => {
        sessionStorage.setItem(DEV_ENTERED_KEY, 'true');
        setEntered(true);
      }}
    />
  );
}

// Dev: skip MSAL, talk to the dev-bypass API. Prod: wrap in MSAL + real tokens.
const tree: ReactNode = authDisabled ? (
  <QueryClientProvider client={queryClient}>
    <DevApiProvider>
      <DevLandingGate>{app}</DevLandingGate>
    </DevApiProvider>
  </QueryClientProvider>
) : (
  <AuthProvider config={msalConfig}>
    {/*
      The door renders here — inside `AuthProvider`, because its call to action is
      `loginRedirect`, and outside everything below, because none of it exists before a token.
      `RequireAuth` still owns the decision and the transient redirect-handling state; what the
      fallback replaces is only the built-in sign-in card.
    */}
    <RequireAuth
      appName="Brush Room"
      variant="light"
      scopes={apiScope ? [apiScope] : []}
      fallback={(signIn) => <LandingScreen onSignIn={signIn} />}
    >
      <QueryClientProvider client={queryClient}>
        <MsalApiProvider>{app}</MsalApiProvider>
      </QueryClientProvider>
    </RequireAuth>
  </AuthProvider>
);

createRoot(container).render(<StrictMode>{tree}</StrictMode>);
